/**
 * Role reference data for the user-management screens.
 *
 * Every key here is a Keycloak GROUP NAME exactly as it arrives in the token's `groups`
 * claim — lowercase, single-spaced. The role dropdown, the table chips and the event log
 * all read those raw names, so a key that does not match a real group silently renders
 * without a colour and never appears in the permissions table.
 */

export const ROLE_COLOR_MAP: Record<string, string> = {
  master: "#6CCBE1",
  admin: "#FDA42C",
  "ho accounting": "#6C6C6C",
  "ho accounting supervisor": "#9C6CDB",
  marketing: "#C0CA33",
  "branch manager": "#E36B6B",
  "branch accounting": "#5C69E8",
  "branch finance": "#0E9AA7",
  "branch finance and accounting": "#2E5C9A",
  "branch sales person": "#FF81D3",
  "bdc manager": "#B5651D",
  "bdc operator": "#D9A066",
  // No Keycloak group exists for this on dev or sit, but the backend still admits it on the
  // check-in routes, so historical event-log rows can carry it. Kept so those rows keep a
  // colour; deliberately absent from ROLE_DETAILS, which lists only assignable roles.
  "branch checker": "#45BE77",
};

/**
 * The "สิทธิ์การใช้งาน" reference table — one entry per role a user can actually be
 * assigned, in descending order of reach: head office first, then branch.
 */
export const ROLE_DETAILS = [
  {
    role: "master",
    description:
      "สิทธิ์สูงสุดของระบบ เข้าถึงได้ทุกฟังก์ชันและทุกสาขาโดยไม่ถูกจำกัดด้วยกฎสิทธิ์ใด ๆ รวมถึงการจัดการผู้ใช้งานทั้งหมด สงวนไว้สำหรับผู้บริหารระดับสูงเท่านั้น",
  },
  {
    role: "admin",
    description:
      "ฝ่ายสนับสนุนด้าน IT ดูแลบัญชีผู้ใช้งาน (สร้างผู้ใช้ใหม่ กำหนดตำแหน่งและสาย เปิด/ปิดการใช้งาน) และดูแลข้อมูลหลักของสินค้าและสมุดราคา มองเห็นข้อมูลได้ทุกสาขา แต่ไม่ใช่ผู้ตรวจสอบยอดขายหรือเอกสารทางบัญชี",
  },
  {
    role: "ho accounting",
    description:
      "พนักงานบัญชีสำนักงานใหญ่ มองเห็นข้อมูลของทุกสาขา ตรวจสอบเอกสารและยอดขายที่สาขาส่งเข้ามา ตรวจสอบรายงานการโอนในฝั่งสำนักงานใหญ่ ผูกร้านค้ากับสมุดราคาและดูต้นทุนสินค้าได้แต่แก้สมุดราคาไม่ได้ ออกใบลดหนี้/ใบเพิ่มหนี้ให้ทุกสาขา และเป็นตำแหน่งเดียวที่เข้าถึงงาน e-Tax ได้",
  },
  {
    role: "ho accounting supervisor",
    description:
      "หัวหน้าบัญชีสำนักงานใหญ่ ทำได้ทุกอย่างเหมือนพนักงานบัญชีสำนักงานใหญ่ และเป็นตำแหน่งเดียวที่ปิดศูนย์เซลประจำวัน ส่งรายการกลับให้สาขาแก้ไข และอนุมัติคำขอแก้ไขยอดโอนได้",
  },
  {
    role: "marketing",
    description:
      "ฝ่ายการตลาด ดูแลข้อมูลหลักของสินค้า สมุดราคา และต้นทุนสินค้าของทุกสาขา: ตั้งราคาต่อหน่วยและส่วนลด เลือกสาขาที่ใช้สมุด ทำสำเนาไปทดสอบใน 00-HO แล้วนำไปใช้ และแก้ราคาหน้าซอง ใช้งานบนเว็บเท่านั้น",
  },
  {
    role: "branch manager",
    description:
      "ผู้รับผิดชอบสูงสุดของสาขา ดูแลสต็อกและการจัดของขึ้นรถ ยืนยันความถูกต้องก่อนรถออก ดูภาพรวมยอดขายของสาขาตัวเอง ตรวจสอบรายงานการโอนในฝั่งสาขา อนุมัติคำขอพิมพ์ซ้ำ และออกใบลดหนี้/ใบเพิ่มหนี้ของสาขาตัวเองได้",
  },
  {
    role: "branch accounting",
    description:
      "พนักงานบัญชีประจำสาขา ตรวจสอบและสรุปรายงานการขายของสาขาก่อนส่งสำนักงานใหญ่ ตรวจสอบรายการโอน แก้ไขยอดในสลิปให้ตรงกับหลักฐาน แนบสลิปแทนพนักงานขาย และออกใบลดหนี้/ใบเพิ่มหนี้ของสาขาตัวเองได้",
  },
  {
    role: "branch finance",
    description:
      "พนักงานการเงินประจำสาขา ทำหน้าที่ตรวจของเสียและของเหลือจริงเมื่อรถกลับเข้าสาขาตอนเย็นผ่านแอปบนมือถือ (PWA) และมีสิทธิ์ตรวจสอบรายการโอน แก้ไขยอดสลิป และออกใบลดหนี้/ใบเพิ่มหนี้ของสาขาตัวเองเช่นเดียวกับบัญชีสาขา",
  },
  {
    role: "branch finance and accounting",
    description:
      "ตำแหน่งรวมสำหรับสาขาที่มีคนเดียวรับผิดชอบทั้งงานบัญชีและงานการเงิน ได้สิทธิ์ของทั้งสองตำแหน่งรวมกัน",
  },
  {
    role: "branch sales person",
    description:
      "พนักงานขายประจำสายส่ง รับผิดชอบสินค้าบนรถ ขายหน้าร้าน และยืนยันข้อมูลผ่านแอปบนมือถือ (PWA) เท่านั้น ไม่สามารถเข้าใช้งานระบบบนเว็บได้",
  },
  {
    role: "bdc manager",
    description:
      "หัวหน้า BDC (ศูนย์กระจายสินค้า 7-Eleven) ใช้งานได้เฉพาะเมนู BDC บนเว็บ: นำเข้าไฟล์ คิวงาน รายละเอียด shipment พิมพ์และส่งออก รวมถึงแก้ไขข้อมูลหลัก BDC (DC และ SKU) ไม่เห็นเมนูอื่นของ GDP และไม่สามารถใช้งานแอปบนมือถือ (PWA) ได้",
  },
  {
    role: "bdc operator",
    description:
      "เจ้าหน้าที่ BDC ใช้งานได้เฉพาะเมนู BDC บนเว็บ: นำเข้าไฟล์ คิวงาน รายละเอียด shipment พิมพ์และส่งออก ดูหน้าตั้งค่า BDC ได้แต่แก้ไขไม่ได้ ไม่เห็นเมนูอื่นของ GDP และไม่สามารถใช้งานแอปบนมือถือ (PWA) ได้",
  },
];
